import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { whitelistQueryString } from '../queryStringUtils';

const isSameOrigin = () => {
  try {
    return !!window.parent.location.hostname;
  } catch {
    return false;
  }
};

const onSameOriginHistoryChange = ({ pathname, search }: { pathname: string; search: string }) => {
  // Replace history state instead of mutating the parent hash not to break
  // the back button. Doing the latter would require the user to press back
  // twice to affect the iframe location.
  // Note that the state and title parameters are set to undefined. It is a
  // bit uncertain the different browser quirks how this will be handled.
  const url = `${window.parent.location.pathname}#${pathname}${whitelistQueryString(search)}`;
  // FIXME: Not sure if this is correct as there's a type error, but I didn't want to mess with ostensibly working code.
  // @ts-expect-error
  window.parent.history.replaceState(undefined, undefined, url);
};

const onCrossOriginHistoryChange = ({ pathname, search }: { pathname: string; search: string }) => {
  window.parent.postMessage(pathname + whitelistQueryString(search), '*');
};

const useParentHistoryMutation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const prevLocationRef = useRef(location);
  const isInitializedRef = useRef(false);

  // Initial sync - run once on mount
  useEffect(() => {
    if (!isInitializedRef.current && window !== window.parent) {
      if (isSameOrigin()) {
        // eslint-disable-next-line @typescript-eslint/no-deprecated
        if (window.parent.location.hash.substr(2) !== '') {
          // eslint-disable-next-line @typescript-eslint/no-deprecated
          void navigate(window.parent.location.hash.substr(1));
        }
      }
      isInitializedRef.current = true;
    }
  }, [navigate]);

  // Sync on location changes
  useEffect(() => {
    if (isInitializedRef.current && prevLocationRef.current !== location) {
      if (window !== window.parent) {
        const handler = isSameOrigin() ? onSameOriginHistoryChange : onCrossOriginHistoryChange;
        handler({ pathname: location.pathname, search: location.search });
      }
      prevLocationRef.current = location;
    }
  }, [location]);
};

export default useParentHistoryMutation;
